import { Fragment, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  defaultReportMonth,
  defaultReportYear,
  formatReportValue,
  reportDefinitionByPath,
  type Permission,
  type ReportColumn,
  type ReportDetail,
  type ReportDocument,
  type ReportExport,
  type ReportExportFormat,
  type ReportKey,
  type ReportLink,
  type ReportTable,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { contractPath } from '@/lib/paths';
import { TONE_CELL, TONE_SWATCH } from '@/lib/report-tones';
import { BillingByMonthChart } from '@/components/BillingByMonthChart';
import { BurndownChart } from '@/components/BurndownChart';
import { CapacityCalendar } from '@/components/CapacityCalendar';
import { CompanyCvrLookup } from '@/components/CompanyCheck';
import { PipelineCharts } from '@/components/PipelineCharts';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardTitle,
  Input,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { EmptyState, MetricCard, PageHeader, Pagination } from '@/components/domain';
import { cn } from '@/lib/cn';

/**
 * Én rapport paa /rapporter/:path - alle tegnes af den samme side.
 *
 * Serveren bygger rapporten som et `ReportDocument` - noegletal, afsnit og forbehold - og siden
 * tegner det, som CSV-filen og PDF'en goer. Der er ingen regnestykker her, kun opstilling: jf.
 * CLAUDE.md hoerer beregningerne i backend, og formateringen er `formatReportValue` fra
 * @codepass/shared, den samme som PDF'en bruger.
 *
 * **Forbeholdene staar under tallene**, ikke gemt bag en knap. En rapport, hvor "faktureret" er
 * nul, fordi alle fakturaer er kladder, skal sige det paa den side, tallet staar.
 */

/** Filen fra serveren som en download. Base64 i JSON, samme form som fakturaens PDF. */
function download(file: ReportExport): void {
  const binary = atob(file.content_base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);

  const url = URL.createObjectURL(new Blob([bytes], { type: file.content_type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = file.file_name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  /* Efter klikket, ikke foer: browseren skal have naaet at starte downloaden. */
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const isNumeric = (column: ReportColumn): boolean => column.type !== 'text';

/**
 * A report whose page has a view of its own above the sections - and, with `replaces`, in place of
 * one of them. The capacity report's 12-week table becomes a calendar one can scroll through; the
 * monthly billing gets its bars against the budget. The CSV and the PDF keep the tables.
 */
const PAGE_VIEWS: Partial<
  Record<ReportKey, { replaces?: string; View: ComponentType<{ report: ReportDocument }> }>
> = {
  capacity: { replaces: 'calendar', View: CapacityCalendar },
  'billing-by-month': { View: BillingByMonthChart },
  pipeline: { View: PipelineCharts },
  burndown: { replaces: 'weeks', View: BurndownChart },
  'company-checks': { View: CompanyCvrLookup },
};

function toneClass(section: ReportTable, row: number, key: string): string | undefined {
  const tone = section.cell_tones?.[row]?.[key];
  return tone ? TONE_CELL[tone] : undefined;
}

/** Where a report's link goes - or null when the user may not open it. Same guards as App.tsx. */
function linkPath(
  link: ReportLink,
  canAny: (...permissions: Permission[]) => boolean,
): string | null {
  switch (link.kind) {
    case 'contract':
      return canAny('contracts.view', 'service_agreements.view')
        ? contractPath({ id: link.contract_no, contract_no: link.contract_no })
        : null;
  }
}

function ReportSection({ section, currency }: { section: ReportTable; currency: string }) {
  const total = section.total;
  const { canAny } = useAuth();
  const [offset, setOffset] = useState(0);
  /** The rows whose details are open - by index in the whole table, so a new page keeps them. */
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
  const toggle = (row: number): void =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(row)) next.delete(row);
      else next.add(row);
      return next;
    });
  /*
   * Paged only when the report asks for it - see `page_size`. A shorter table after a new year
   * or month starts over rather than on a page that is no longer there.
   */
  const pageSize = section.page_size;
  const start = pageSize && offset < section.rows.length ? offset : 0;
  const rows = pageSize ? section.rows.slice(start, start + pageSize) : section.rows;

  return (
    <Card className="mb-5">
      <CardHeader>
        <CardTitle>{section.title}</CardTitle>
        {section.description ? (
          <p className="mt-1 text-xs text-ink-muted">{section.description}</p>
        ) : null}
      </CardHeader>

      {section.rows.length === 0 ? (
        <EmptyState title={section.empty} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                {section.columns.map((column) => (
                  <Th
                    key={column.key}
                    className={cn(isNumeric(column) && 'text-right', 'whitespace-nowrap')}
                  >
                    {column.label}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const at = start + index;
                const detail = section.details?.rows[at] ?? null;
                const expanded = open.has(at);
                return (
                  <Fragment key={at}>
                    <Tr>
                      {section.columns.map((column) => {
                        const text = formatReportValue(
                          column.type,
                          row[column.key] ?? null,
                          currency,
                        );
                        const link = section.links?.[at]?.[column.key];
                        const path = link ? linkPath(link, canAny) : null;
                        let content: ReactNode = text;
                        if (path) {
                          content = (
                            <Link to={path} className="text-brand hover:underline">
                              {text}
                            </Link>
                          );
                        }
                        if (section.details?.column === column.key && detail?.rows.length) {
                          content = (
                            <button
                              type="button"
                              aria-expanded={expanded}
                              onClick={() => toggle(at)}
                              className="font-medium text-brand underline-offset-2 hover:underline"
                            >
                              {text}
                            </button>
                          );
                        }
                        return (
                          <Td
                            key={column.key}
                            className={cn(
                              'text-xs',
                              isNumeric(column) && 'whitespace-nowrap text-right tabular',
                              toneClass(section, at, column.key),
                            )}
                          >
                            {content}
                          </Td>
                        );
                      })}
                    </Tr>
                    {expanded && detail ? (
                      <tr>
                        <td
                          colSpan={section.columns.length}
                          className="border-b border-line bg-canvas px-4 py-3"
                        >
                          <ReportDetailTable detail={detail} currency={currency} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {total ? (
                <tr className="bg-canvas font-semibold">
                  {section.columns.map((column, index) => (
                    <Td
                      key={column.key}
                      className={cn(
                        'text-xs',
                        isNumeric(column) && 'whitespace-nowrap text-right tabular',
                      )}
                    >
                      {index === 0
                        ? 'I alt'
                        : total[column.key] === undefined
                          ? null
                          : formatReportValue(column.type, total[column.key] ?? null, currency)}
                    </Td>
                  ))}
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      )}

      {pageSize ? (
        <Pagination
          total={section.rows.length}
          limit={pageSize}
          offset={start}
          onChange={setOffset}
        />
      ) : null}

      {section.legend && section.legend.length > 0 && section.rows.length > 0 ? (
        <ul className="flex flex-wrap gap-x-5 gap-y-1 border-t border-line px-5 py-3 text-xs text-ink-muted">
          {section.legend.map((entry) => (
            <li key={entry.tone} className="flex items-center gap-1.5">
              <span aria-hidden className={cn('h-3 w-3 rounded-sm', TONE_SWATCH[entry.tone])} />
              {entry.label}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** The rows behind a number, opened under its row - see `ReportTable.details`. */
function ReportDetailTable({ detail, currency }: { detail: ReportDetail; currency: string }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-ink">{detail.title}</p>
      <table className="w-full text-xs">
        <thead>
          <tr>
            {detail.columns.map((column) => (
              <th
                key={column.key}
                className={cn(
                  'border-b border-line px-2 py-1 font-semibold text-ink-muted',
                  isNumeric(column) ? 'text-right' : 'text-left',
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.rows.map((row, index) => (
            <tr key={index}>
              {detail.columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    'border-b border-line/60 px-2 py-1',
                    isNumeric(column) && 'whitespace-nowrap text-right tabular',
                  )}
                >
                  {formatReportValue(column.type, row[column.key] ?? null, currency)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReportPage() {
  const { path } = useParams();
  const { can, canSeeReport } = useAuth();
  const definition = reportDefinitionByPath(path ?? '');
  /* Rapporterne vaelges pr. rolle paa /admin/roller. */
  const allowed = Boolean(definition && canSeeReport(definition.key));
  /* The CSV and PDF are 'Eksportér rapporter' - the API refuses the file without it. */
  const canExport = can('reports.export');

  /*
   * Maaneden pr. rapport, med rapportens egen standard - se `defaultMonth` i @codepass/shared.
   * Siden bliver monteret, naar man skifter rapport, saa ét felt ville tage maanedsrapportens
   * sidste maaned med over paa aktivitetsrapporten, der skal aabne paa den igangvaerende.
   */
  const [months, setMonths] = useState<Record<string, string>>({});
  const month = definition
    ? (months[definition.key] ?? defaultReportMonth(definition, new Date()))
    : '';
  const setMonth = (value: string): void => {
    if (!definition) return;
    setMonths((current) => ({ ...current, [definition.key]: value }));
  };
  const monthParam = definition?.period === 'month' ? month : undefined;

  /* The year on a yearly report, the same way - see `defaultReportYear`. */
  const thisYear = defaultReportYear(new Date());
  const [years, setYears] = useState<Record<string, number>>({});
  const year = definition ? (years[definition.key] ?? thisYear) : thisYear;
  const yearParam = definition?.period === 'year' ? String(year) : undefined;
  const pageView = definition ? PAGE_VIEWS[definition.key] : undefined;

  /*
   * The choice on a report that has one - the burndown's project, see `ReportChoice`. The server
   * lists the options and picks the first; what is chosen here is sent back as `?<param>=`. Per
   * report, like the month.
   */
  const [choices, setChoices] = useState<Record<string, { param: string; value: string }>>({});
  const chosen = definition ? choices[definition.key] : undefined;
  const choiceParams = chosen ? { [chosen.param]: chosen.value } : {};

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'report',
      definition?.key,
      monthParam ?? null,
      yearParam ?? null,
      chosen?.value ?? null,
    ],
    queryFn: () =>
      api.get<ReportDocument>(`/reports/${definition?.key}`, {
        month: monthParam,
        year: yearParam,
        ...choiceParams,
      }),
    enabled: allowed,
    /*
     * The project before stays on screen while the next one loads, so the picker does not vanish -
     * but only within the same report, never another report's numbers under this one's title.
     */
    placeholderData: (previous, query) =>
      query?.queryKey[1] === definition?.key ? previous : undefined,
  });

  const exportFile = useMutation({
    mutationFn: (format: ReportExportFormat) =>
      api.get<ReportExport>(`/reports/${definition?.key}/export`, {
        month: monthParam,
        year: yearParam,
        ...choiceParams,
        format,
      }),
    onSuccess: download,
  });

  if (!definition) {
    return (
      <Card>
        <EmptyState
          title="Rapporten findes ikke"
          action={
            <Link to="/rapporter">
              <Button size="sm" variant="secondary">
                Til rapporterne
              </Button>
            </Link>
          }
        />
      </Card>
    );
  }

  if (!allowed) {
    return <Alert tone="error">Din rolle giver ikke adgang til denne rapport.</Alert>;
  }

  return (
    <>
      <PageHeader
        title={definition.title}
        description={data?.period_label ?? definition.description}
        actions={
          <>
            {data?.choice && data.choice.options.length > 0 ? (
              <Select
                className="w-64 max-w-full"
                value={chosen?.value ?? data.choice.selected ?? ''}
                aria-label={data.choice.label}
                onChange={(event) => {
                  const choice = data.choice!;
                  setChoices((current) => ({
                    ...current,
                    [definition.key]: { param: choice.param, value: event.target.value },
                  }));
                }}
              >
                {data.choice.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            ) : null}
            {definition.period === 'month' ? (
              <Input
                type="month"
                className="w-40"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                aria-label="Måned"
              />
            ) : null}
            {definition.period === 'year' ? (
              <Select
                className="w-28"
                value={year}
                aria-label="År"
                onChange={(event) =>
                  setYears((current) => ({
                    ...current,
                    [definition.key]: Number(event.target.value),
                  }))
                }
              >
                {/* Fem år tilbage og det næste - et budget kan ligge i BC, før året begynder. */}
                {Array.from({ length: 7 }, (_, index) => thisYear + 1 - index).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            ) : null}
            {canExport ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={exportFile.isPending && exportFile.variables === 'csv'}
                  disabled={!data}
                  onClick={() => exportFile.mutate('csv')}
                >
                  Hent CSV
                </Button>
                <Button
                  size="sm"
                  loading={exportFile.isPending && exportFile.variables === 'pdf'}
                  disabled={!data}
                  onClick={() => exportFile.mutate('pdf')}
                >
                  Hent PDF
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {exportFile.error ? (
        <Alert tone="error" className="mb-4">
          Kunne ikke danne filen. {exportFile.error.message}
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : error || !data ? (
        <Alert tone="error">Kunne ikke hente rapporten.</Alert>
      ) : (
        <>
          <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {data.kpis.map((kpi) => (
              <MetricCard
                key={kpi.label}
                label={kpi.label}
                value={formatReportValue(kpi.type, kpi.value, data.currency)}
                hint={kpi.hint}
                tone={kpi.tone}
              />
            ))}
          </div>

          {pageView ? <pageView.View report={data} /> : null}

          {data.sections
            .filter((section) => section.id !== pageView?.replaces)
            .map((section) => (
              <ReportSection key={section.id} section={section} currency={data.currency} />
            ))}

          {data.notes.length > 0 ? (
            <Alert tone="info" title="Forbehold">
              <ul className="list-disc space-y-1 pl-4">
                {data.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </>
      )}
    </>
  );
}
